import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, first, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { ControlMessage } from '../interface/control-message';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../component/dialog/confirmation-dialog/confirmation-dialog.component';
import { DataService } from './data.service';
import { ControlMessageType } from '../interface/enum-controlMessages-type';

@Injectable({
  providedIn: 'root'
})
export class ControlMessageService {

  /**
   * Control messages are scoped to the company the user is logged into, never to the matrix.
   * Each store carries its own CRM connection (`companies.tokenWhats`) and the dispatch jobs
   * resolve that token from `controlMessage.company`, so a branch's configuration saved under
   * the matrix would go out on the matrix's connection and carry the matrix's indicators.
   * The recipient picker (`UserService`) already queries the logged company.
   */
  private _isFromMatrix = false;
  private apiUrl = `${environment.mkgoURL}/api/v1/control-messages`;

  // Subject para manter as mensagens de controle
  private messagesSubject: BehaviorSubject<ControlMessage[]> = new BehaviorSubject<ControlMessage[]>([]);
  // Observable para permitir que outros componentes se inscrevam para receber atualizações das mensagens
  messages$: Observable<ControlMessage[]> = this.messagesSubject.asObservable();

  private _loadSequence = 0;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private dataService: DataService
  ) {
    this.dataService.company$.pipe(
      filter(Boolean),
      map(company => company.id),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => {
      this.messagesSubject.next([]);
      this.loadMessages();
    });
  }

  async loadMessages(): Promise<ControlMessage[]> {
    const sequence = ++this._loadSequence;
    try {
      const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
      const response = await firstValueFrom(this.http.get<{ controlMessages: ControlMessage[] }>(this.apiUrl, header));
      const messages = (response?.controlMessages ?? []).map(message => {
        if (message?.message) {
          message.message = this.fromUnicode(message.message);
        }
        return message;
      });
      if (sequence === this._loadSequence) {
        this.messagesSubject.next(messages);
      }
      return messages;
    } catch (error) {
      console.error('Erro ao carregar mensagens de controle', error);
      if (sequence === this._loadSequence) {
        this.loadExampleMessages();
      }
      return [];
    }
  }


  // Método para carregar as mensagens de exemplo
  private loadExampleMessages(): void {
    this.messagesSubject.next([]);
  }

  // Método para atualizar uma mensagem
  async updateMessage(updatedMessage: ControlMessage): Promise<void> {
    try {
      const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
      const response = await firstValueFrom(this.http.put(`${this.apiUrl}/${updatedMessage._id}`, updatedMessage, header));

      const messages = this.messagesSubject.getValue();
      const index = messages.findIndex(msg => msg._id === updatedMessage._id);
      if (index !== -1) {
        messages[index] = updatedMessage;
        this.messagesSubject.next([...messages]);
      }

      return;
    } catch (error) {
      console.error('Erro ao atualizar a mensagem', error);
    }
  }


  async deleteMessage(messageId: string) {
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    return this.http.delete(`${this.apiUrl}/${messageId}`, header).pipe(first()).toPromise();
  }

  private fromUnicode(value: string): string {
    return value.replace(/\\u[\dA-F]{4}/gi, (match) => {
      return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
    });
  }


  private toAPI(controlMessage: ControlMessage): ControlMessage {
    delete controlMessage._id;

    if (controlMessage.time === undefined || controlMessage.time === null) {
      delete controlMessage.time;
    }
    
    // Converte emojis para string Unicode em cada mensagem
    if (controlMessage.message) {
      controlMessage.message = this.convertEmojiToUnicode(controlMessage.message);
    }
    
    // Processa o array de variações de mensagens, se existir
    if (controlMessage.messageVariations && Array.isArray(controlMessage.messageVariations)) {
      controlMessage.messageVariations = controlMessage.messageVariations.map(msg => 
        this.convertEmojiToUnicode(msg)
      );
    }

    return controlMessage;
  }

  // Função auxiliar para converter emojis em uma única mensagem
  private convertEmojiToUnicode(message: string): string {
    if (!message) return message;
    
    return message.split('')
      .map(char => {
        const codePoint = char.codePointAt(0);
        // Check if the character is an emoji (partial range as an example)
        if ((codePoint >= 0x1F600 && codePoint <= 0x1F64F) || // Emoticons
            (codePoint >= 0x1F300 && codePoint <= 0x1F5FF) || // Miscellaneous Symbols and Pictographs
            (codePoint >= 0x1F680 && codePoint <= 0x1F6FF) || // Transport and Map Symbols
            (codePoint >= 0x2600 && codePoint <= 0x26FF)) {   // Miscellaneous Symbols
          return '\\u' + codePoint.toString(16).padStart(4, '0');
        }
        return char; // Leave other characters as-is
      })
      .join('');
  }


  /**
   * Salva ou atualiza uma mensagem de controle
   * @param message A mensagem a ser salva ou atualizada
   */
  async saveMessage(message: ControlMessage): Promise<void> {
    const messages = this.messagesSubject.getValue();
    let existingMessage: ControlMessage | undefined;

    // ETAPA 1: Determinar se devemos atualizar uma mensagem existente ou criar uma nova
    
    // Caso a mensagem já tenha um ID, estamos editando uma mensagem existente
    if (message._id) {
      existingMessage = message;
    } 
    // Caso estejamos criando uma nova mensagem
    else {
      // Cada tipo de mensagem tem regras específicas para buscar uma existente
      const messageType = message.type;
      
      if (messageType === ControlMessageType.DueTitleMessage || 
          messageType === ControlMessageType.Charge) {
        // DueTitleMessage e CHARGE: sempre cria uma nova
        existingMessage = undefined;
      } 
      else if (messageType === ControlMessageType.Statistics) {
        // Statistics: verifica se existe com a mesma frequência
        existingMessage = messages.find(msg => 
          msg.type === messageType && 
          msg.frequency === message.frequency
        );
      } 
      else {
        // Outros tipos: busca pelo tipo
        existingMessage = messages.find(msg => msg.type === messageType);
      }
    }

    // ETAPA 2: Processar a ação adequada (atualizar ou criar)
    
    // Se encontramos uma mensagem existente, atualizamos
    if (existingMessage?._id) {
      message._id = existingMessage._id;
      await this.updateMessage(message);
    } 
    // Caso contrário, criamos uma nova
    else {
      try {
        const messageToSave = this.toAPI(message);
        const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
        
        const response = await firstValueFrom(
          this.http.post(this.apiUrl, messageToSave, header)
        );
        
        // Processamos a resposta bem-sucedida
        if (message?.message) {
          message.message = this.fromUnicode(message.message);
        }
        
        // Adicionamos o ID retornado pela API
        if (response && response['_id']) {
          message._id = response['_id'];
        }
        
        // Atualizamos a lista local
        messages.push(message);
        this.messagesSubject.next([...messages]);
      } 
      catch (error) {
        console.error('Erro ao salvar a mensagem', error);
        this.openConfirmationDialog();
      }
    }
  }

  // Método para abrir um dialog de confirmação
  private openConfirmationDialog(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        text: 'FALHA_CRIAR_MENSAGEM',
        uniqueAction: true
      }
    });
  }

  // Método para buscar uma mensagem pelo tipo
  getMessageByType(type: ControlMessageType): ControlMessage | undefined {
    const messages = this.messagesSubject.getValue();
    return messages.find(msg => msg.type === type);
  }

  // Método para obter uma mensagem pelo typeId
  getMessageByTypeId(typeId: number): ControlMessage | undefined {
    const messages = this.messagesSubject.getValue();
    return messages.find(message => message.type === typeId);
  }

  /**
   * Retorna uma lista de frequências já utilizadas para mensagens Statistics
   * @param currentMessageId ID da mensagem atual sendo editada (para excluir da verificação)
   * @returns Array com as frequências já utilizadas
   */
  getUsedStatisticsFrequencies(currentMessageId?: string): string[] {
    const messages = this.messagesSubject.getValue();
    const statisticsMessages = messages.filter(msg => 
      msg.type === ControlMessageType.Statistics && 
      // Exclui a mensagem atual quando estiver editando
      (currentMessageId ? msg._id !== currentMessageId : true)
    );
    
    // Extrai as frequências únicas já usadas
    return [...new Set(statisticsMessages.map(msg => msg.frequency))];
  }
}
